import { useMemo } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { RECOMMENDED_PLAYBOOK } from '@forwardflow/features/campaign-flow/data/mock'
import {
   IFlowDraft,
   IPlaybook,
   TEditTab,
   TFlowStep,
   TLeadType,
   TRailKey,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { audienceTypes } from '@forwardflow/features/campaign-flow/utils/describe'
import {
   viewFor,
   writeFor,
} from '@forwardflow/features/campaign-flow/utils/playbook'

export const FLOW_STEPS: TFlowStep[] = [
   'who',
   'handoff',
   'voice',
   'launch',
   'launching',
   'playbook',
   'numbers',
]

/** The three questions, in order. Launch comes after them. */
export const ASKED: TFlowStep[] = ['who', 'handoff', 'voice', 'launch']

export const RAIL_OF: Record<TFlowStep, TRailKey> = {
   who: 'who',
   handoff: 'handoff',
   voice: 'voice',
   launch: 'launch',
   launching: 'launch',
   playbook: 'playbook',
   numbers: 'numbers',
}

export const emptyDraft = (): IFlowDraft => ({
   id: null,
   status: 'new',
   audience: {
      description: '',
      name: '',
      kinds: [],
      leadTypes: [],
      filters: [],
      leadCount: null,
   },
   playbook: JSON.parse(JSON.stringify(RECOMMENDED_PLAYBOOK)) as IPlaybook,
   voice: { optionId: 'Kore', name: 'Erin' },
   numbers: { areaCodes: [], selected: [] },
   handoff: { mode: 'round_robin', agentIds: [] },
})

interface IFlowState {
   draft: IFlowDraft
   step: TFlowStep
   /** Where Playbook / Numbers return to when the user is done there. */
   returnTo: TFlowStep
   editTab: TEditTab
   /** Whose words the playbook editor shows when the campaign calls several lead types. */
   editType: TLeadType | null
   /**
    * With several lead types, the playbook asks whose words to edit first;
    * true once the user has picked (reset when the editor closes).
    */
   typePicked: boolean
   /** Questions the user has answered (rail shows a check and the answer). */
   done: TRailKey[]
   /** Snapshot taken when the playbook editor opens; Discard restores it. */
   playbookBefore: IPlaybook | null
   /** The "Act as lead" drawer. */
   phoneOpen: boolean
   saving: boolean

   setStep: (step: TFlowStep) => void
   /** Open an optional screen (playbook, numbers) and remember where to come back. */
   openSide: (step: 'playbook' | 'numbers', tab?: TEditTab) => void
   closeSide: () => void
   setEditTab: (tab: TEditTab) => void
   setEditType: (type: TLeadType) => void
   /** Back to "whose playbook?" when several lead types share the campaign. */
   pickTypeAgain: () => void
   setPhoneOpen: (open: boolean) => void
   markDone: (key: TRailKey) => void
   update: (fn: (d: IFlowDraft) => IFlowDraft) => void
   /** Open a saved campaign: its draft, with the questions it already answers ticked. */
   load: (draft: IFlowDraft, done: TRailKey[]) => void
   /** Edit the playbook as the lead type being edited sees it; timing is shared. */
   updatePlaybook: (fn: (p: IPlaybook) => IPlaybook) => void
   discardEdits: () => void
   setSaving: (saving: boolean, id?: string) => void
   reset: () => void
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T

export const useCampaignFlowStore = create<IFlowState>()(
   persist(
      (set, get) => ({
         draft: emptyDraft(),
         step: 'who',
         returnTo: 'who',
         editTab: 'cadence',
         editType: null,
         typePicked: false,
         done: [],
         playbookBefore: null,
         phoneOpen: false,
         saving: false,

         setStep: (step) => set({ step }),
         openSide: (step, tab) =>
            set((s) => ({
               step,
               returnTo:
                  s.step === 'playbook' || s.step === 'numbers'
                     ? s.returnTo
                     : s.step === 'launching'
                       ? 'launch'
                       : s.step,
               editTab: tab ?? s.editTab,
               playbookBefore:
                  step === 'playbook'
                     ? (s.playbookBefore ?? clone(s.draft.playbook))
                     : s.playbookBefore,
            })),
         /* Done with Playbook or Numbers: the first unanswered question, else launch. */
         closeSide: () =>
            set((s) => ({
               step:
                  ASKED.find(
                     (x) => x !== 'launch' && !s.done.includes(RAIL_OF[x])
                  ) ?? 'launch',
               playbookBefore: null,
               phoneOpen: false,
               typePicked: false,
            })),
         setEditTab: (editTab) => set({ editTab }),
         setEditType: (editType) => set({ editType, typePicked: true }),
         pickTypeAgain: () => set({ typePicked: false }),
         setPhoneOpen: (phoneOpen) => set({ phoneOpen }),
         markDone: (key) =>
            set((s) => ({
               done: s.done.includes(key) ? s.done : [...s.done, key],
            })),
         update: (fn) => set((s) => ({ draft: fn(s.draft) })),
         load: (draft, done) =>
            set({
               draft,
               done,
               step: 'who',
               returnTo: 'who',
               editType: null,
               typePicked: false,
               playbookBefore: null,
               phoneOpen: false,
               saving: false,
            }),
         updatePlaybook: (fn) =>
            set((s) => {
               const types = audienceTypes(s.draft.audience)
               const type = activeType(s)
               return {
                  draft: {
                     ...s.draft,
                     playbook: writeFor(
                        s.draft.playbook,
                        type,
                        fn(viewFor(s.draft.playbook, type)),
                        types
                     ),
                  },
               }
            }),
         discardEdits: () => {
            const before = get().playbookBefore
            if (before)
               set((s) => ({ draft: { ...s.draft, playbook: clone(before) } }))
         },
         setSaving: (saving, id) =>
            set((s) => ({
               saving,
               draft: id
                  ? {
                       ...s.draft,
                       id,
                       status:
                          s.draft.status === 'new' ? 'draft' : s.draft.status,
                    }
                  : s.draft,
            })),
         reset: () =>
            set({
               draft: emptyDraft(),
               step: 'who',
               returnTo: 'who',
               editTab: 'cadence',
               editType: null,
               typePicked: false,
               done: [],
               playbookBefore: null,
               phoneOpen: false,
               saving: false,
            }),
      }),
      {
         name: 'ff-campaign-flow',
         version: 6,
         storage: createJSONStorage(() => sessionStorage),
         partialize: (s) => ({
            draft: s.draft,
            step: s.step,
            returnTo: s.returnTo,
            editTab: s.editTab,
            editType: s.editType,
            typePicked: s.typePicked,
            done: s.done,
            playbookBefore: s.playbookBefore,
         }),
         migrate: () => ({}) as never,
      }
   )
)

/** The lead type being edited: the chosen one, or the first lead type of the audience. */
export const activeType = (s: {
   editType: TLeadType | null
   draft: IFlowDraft
}): TLeadType => {
   const types = audienceTypes(s.draft.audience)
   return s.editType && types.includes(s.editType)
      ? s.editType
      : (types[0] ?? 'buyer')
}

/** The playbook with the words of the lead type being edited. */
export const usePlaybookView = () => {
   const playbook = useCampaignFlowStore((s) => s.draft.playbook)
   const type = useCampaignFlowStore(activeType)
   return useMemo(() => viewFor(playbook, type), [playbook, type])
}
